
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsx mdx */
import { loremIpsum } from "lorem-ipsum";
import {
  CodeBlock,
  ComponentDocs,
  Preview,
} from "@lighting-beetle/lighter-styleguide";
import { Table } from "../";
import { TabPanel, Tabs } from "./";

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
  return <div {...props}/>
};
const Code = makeShortcode("Code");
const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1>{`Tabs`}</h1>
    <p>{`Navigate sections of content in a tabbed interface.`}</p>
    <h2>{`Example`}</h2>
    <Tabs mdxType="Tabs">
  {[1, 2, 3].map(item => <TabPanel key={item} id={`tabpanel-example-${item}`} tab={`Tab ${item}`} mdxType="TabPanel">
      <p>{loremIpsum({
            count: 3
          })}</p>
    </TabPanel>)}
    </Tabs>
    <h2>{`Variants`}</h2>
    <h3>{`Basic`}</h3>
    <Preview code={<Tabs activeTabIndex={2} mdxType="Tabs">
      <TabPanel id="tabpanel-1" tab="Tab 1" mdxType="TabPanel">
        ...
      </TabPanel>
      <TabPanel id="tabpanel-2" tab="Tab 2" mdxType="TabPanel">
        ...
      </TabPanel>
      <TabPanel id="tabpanel-3" tab="Tab 3" mdxType="TabPanel">
        ...
      </TabPanel>
      <TabPanel id="tabpanel-4" isDisabled tab="Tab 4" mdxType="TabPanel">
        ...
      </TabPanel>
    </Tabs>} mdxType="Preview">
  <Tabs activeTabIndex={2} mdxType="Tabs">
    {[1, 2, 3, 4].map(item => <TabPanel key={item} id={`tabpanel-${item}`} tab={`Tab ${item}`} isDisabled={item === 4 ? true : null} mdxType="TabPanel">
        <p>{loremIpsum({
              count: 5
            })}</p>
      </TabPanel>)}
  </Tabs>
    </Preview>
    <h3>{`Full width equal tabs`}</h3>
    <Preview code={<Tabs equalTabWidth mdxType="Tabs">
      <TabPanel id="tabpanel-equal-1" tab="Tab 1" mdxType="TabPanel">
        ...
      </TabPanel>
      <TabPanel id="tabpanel-equal-2" tab="Tab 2" mdxType="TabPanel">
        ...
      </TabPanel>
      <TabPanel id="tabpanel-equal-3" tab="Tab 3" mdxType="TabPanel">
        ...
      </TabPanel>
      <TabPanel id="tabpanel-equal-4" tab="Tab 4" mdxType="TabPanel">
        ...
      </TabPanel>
    </Tabs>} mdxType="Preview">
  <Tabs equalTabWidth mdxType="Tabs">
    {[5, 6, 7, 8].map(item => <TabPanel key={item} id={`tabpanel-equal-${item}`} tab={`Tab ${item}`} isActive={item === 7} mdxType="TabPanel">
        <p>{loremIpsum({
              count: 5
            })}</p>
      </TabPanel>)}
  </Tabs>
    </Preview>
    <h2>{`Props`}</h2>
    <ComponentDocs title="<Tabs />" component={Tabs} mdxType="ComponentDocs" />
    <ComponentDocs title="<TabPanel />" component={TabPanel} excludes={["isActive"]} mdxType="ComponentDocs" />
    <h2>{`JS module reference`}</h2>
    <p>{`All elements with `}<inlineCode parentName="p">{`[role="tab"]`}</inlineCode>{` will be initialised automatically.`}</p>
    <h3>{`Custom initialization`}</h3>
    <CodeBlock language="jsx" mdxType="CodeBlock">
  {`
  const myTabs = new window.myApp.Select( document.getElementById('my-tabs'), config );
  `}
    </CodeBlock>
    <h3>{`Default config`}</h3>
    <Table className="text-small" columns={[{
      key: "col1",
      label: "Name."
    }, {
      key: "col2",
      label: "type"
    }, {
      key: "col3",
      label: "Description"
    }]} data={[{
      col1: "tabSelector",
      col2: "string",
      col3: <React.Fragment>
          <Code mdxType="Code">[role="tab"]</Code> Tab element selector.
        </React.Fragment>
    }, {
      col1: "activeClass",
      col2: "string",
      col3: <React.Fragment>
          <Code mdxType="Code">.is-active</Code> Active class applied to active tab and
          tabpanel.
        </React.Fragment>
    }]} mdxType="Table" />
    <h3>{`Methods`}</h3>
    <Table className="text-small" columns={[{
      key: "col1",
      label: "Name."
    }, {
      key: "col2",
      label: "Description."
    }]} data={[{
      col1: "init",
      col2: "Creates a new instance"
    }, {
      col1: "destroy",
      col2: "Destroy the instance - remove all listeners"
    }, {
      col1: "update",
      col2: "Updates the instance"
    }, {
      col1: "toggleTab(el: element, state: 'on'/'off')",
      col2: "toggle a tab on or off"
    }, {
      col1: "toggleTabPanel(el: element, state: 'on'/'off')",
      col2: "toggle a tabpanel on or off"
    }, {
      col1: "activateNthTab(index: integer)",
      col2: "activates nth (zero based index) tab and tabpanel"
    }, {
      col1: "static getInstance(el: element)",
      col2: 'Get instance of `Tabs` attached to specific DOM element (usually `[role="tablist"]`).'
    }]} mdxType="Table" />
    <h2>{`Accessibility`}</h2>
    <p>{`Tabs are implemented according to `}<a parentName="p" {...{
        "href": "https://www.w3.org/TR/wai-aria-practices/#tabpanel"
      }}>{`WAI-ARIA authoring practices`}</a>{` - `}<a parentName="p" {...{
        "href": "https://www.w3.org/TR/wai-aria-practices/examples/tabs/tabs-2/tabs.html"
      }}>{`Example 2 Tabs with manual activation`}</a></p>
    <p>{`Notes:`}</p>
    <ul>
      <li parentName="ul">{`Optional keyboard interaction (Home, End, Shift + 10, Delete) is not implemented.`}</li>
      <li parentName="ul">{`Tabpanel elements lack aria-labelledby attributes due to a bug in VoiceOver, which reads the label instead of the content of the tabpanel.`}</li>
    </ul>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;